<template>
  <section id="budget-prices-widget">
    <header>
      Бюджетная цена

      <template v-if="product?.isExisting === true">
        <VLink v-if="budgetPrices.length > 0" material="history" @click="BudgetPricesService.showHistory(product)"> История </VLink>
        <VLink material="add" @click="add"> Добавить </VLink>
      </template>
    </header>

    <p v-if="product?.isExisting !== true" class="helper-text">
      Сначала сохраните товар — бюджетную цену можно завести только после его создания
    </p>

    <p v-else-if="budgetPrices.length === 0 && isLoading === false" class="helper-text">
      Бюджетная цена не заведена
    </p>

    <ul v-else>
      <li v-for="budgetPrice in budgetPrices" :key="budgetPrice.id">
        <span class="nowrap">с {{ budgetPrice.validityPeriod.start?.format() }}</span>
        <span class="nowrap">{{ budgetPrice.price.format(budgetPrice.currency, product.unit) }}</span>
        <span v-if="budgetPrice.optimalPurchaseQty !== null" class="nowrap">
          объём {{ budgetPrice.optimalPurchaseQty.formatSmart(product.unit) }}
        </span>
        <VLink material="delete" @click="remove(budgetPrice)"/>
      </li>
    </ul>
  </section>
</template>

<style lang="scss" src="./BudgetPricesWidget.scss"/>

<script lang="ts">
import BudgetPrice         from "@/domains/BudgetPrices/BudgetPrice";
import BudgetPricesService from "@/domains/BudgetPrices/BudgetPricesService";
import type Product        from "@/domains/Products/Product";

/**
 * Бюджетная цена номенклатуры в карточках номенклатуры и разрешения: действующая и будущие записи компактным списком.
 * Записи не редактируются — новая заводится карточкой BudgetPriceCard. Действующую цену отдаёт наружу
 * через v-model:budget-price — её ждёт сетка тарифов разрешения
 */
export default defineComponent({
  name: "BudgetPricesWidget",

  props: {
    product: {
      type: Object as PropType<Product | null>,
      default: null,
    },
  },

  emits: ['update:budgetPrice'],

  setup() {
    return {
      BudgetPricesService,
    }
  },

  data() {
    return {
      /** Действующая и будущие записи в порядке даты начала */
      budgetPrices: [] as BudgetPrice[],
      isLoading: false,
    }
  },

  watch: {
    'product.id': {
      immediate: true,
      handler: 'load',
    },
  },

  methods: {
    async load(): Promise<void> {
      if (this.product?.isExisting === true) {
        this.isLoading = true;

        const products = await BudgetPricesService.getList(this.product.id);

        this.budgetPrices = products[0]?.budgetPrices ?? [];
        this.isLoading = false;

        this.$emit('update:budgetPrice', products[0]?.actualBudgetPrice ?? null);
      }
      else {
        this.budgetPrices = [];

        this.$emit('update:budgetPrice', null);
      }
    },

    async add(): Promise<void> {
      if (await BudgetPricesService.showBudgetPriceCard(this.product) === true) {
        await this.load();
      }
    },

    async remove(budgetPrice: BudgetPrice): Promise<void> {
      if (await BudgetPricesService.deleteWithConfirm(budgetPrice) === true) {
        await this.load();
      }
    },
  },
})
</script>
